// src/utils/editorStorage.js
// localStorage helpers for editor draft content (mirrors canvasStorage.js)
//
// A snapshot here is work the server hasn't confirmed: written as the author
// types, cleared when a save lands. It used to be written and never read back,
// so a lost connection plus a closed tab lost the work anyway. Now a snapshot
// left by an EARLIER visit is offered back (Restore / Discard), the way
// WordPress and MediaWiki do it.
//
// Each editor visit writes under its own key (draft id + a visit id). Nothing
// is ever moved or overwritten across visits: an earlier design moved the old
// snapshot aside, which needed room for a second full copy (and quietly lost
// the work on the largest manuscripts), and let a new visit's first keystroke
// overwrite a snapshot too fresh to offer yet.
//
// Each snapshot records the hash of the server content it builds on, so the
// editor can tell whether the draft was saved after it — restoring would then
// replace that newer version, and the author is told so.

const KEY_PREFIX = 'rv_editor_draft';
// Older copies are dropped: nothing stale should come back months later.
export const LOCAL_DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Cheap fingerprint of draft HTML. The server stores content verbatim, so equal
 * hashes mean "the same version".
 * @param {string} s
 * @returns {string}
 */
export function hashContent(s) {
  const str = String(s ?? '');
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return `${(h >>> 0).toString(16)}:${str.length}`;
}

/** A fresh id for one editor visit. */
export function makeVisitId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

// Snapshots predating visit ids were stored under the bare draft key; they are
// treated as left by an earlier visit.
const keyFor = (draftId, visitId) => (visitId
  ? `${KEY_PREFIX}_${draftId}_${visitId}`
  : `${KEY_PREFIX}_${draftId}`);

const read = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const rec = raw ? JSON.parse(raw) : null;
    return rec && typeof rec.html === 'string' ? rec : null;
  } catch {
    return null;
  }
};
const remove = (key) => {
  try { localStorage.removeItem(key); } catch {}
};

// Every snapshot key belonging to one draft, from any visit.
function draftKeys(draftId) {
  const base = keyFor(draftId);
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key === base || key?.startsWith(`${base}_`)) out.push(key);
    }
  } catch {}
  return out;
}

/**
 * This visit's snapshot.
 * @returns {{ html: string, ts: number, baseHash?: string } | null}
 */
export function getLocalDraft(draftId, visitId) {
  return read(keyFor(draftId, visitId));
}

/**
 * Save this visit's snapshot.
 * @param {string} draftId
 * @param {string} html
 * @param {string} [baseHash] hashContent() of the server version it builds on
 * @param {string} [visitId]
 * @returns {boolean} false when the browser refused (quota, private mode) —
 *          the caller must not then tell the author their work is kept here
 */
export function setLocalDraft(draftId, html, baseHash, visitId) {
  try {
    localStorage.setItem(
      keyFor(draftId, visitId),
      JSON.stringify({ html, ts: Date.now(), baseHash }),
    );
    return true;
  } catch {
    /* quota exceeded or private browsing */
    return false;
  }
}

/** This visit's work reached the server: its snapshot is no longer needed. */
export function clearLocalDraft(draftId, visitId) {
  remove(keyFor(draftId, visitId));
}

/**
 * Snapshots OTHER visits left for this draft. Returns the newest one at least
 * `minAgeMs` old to offer, and whether a younger one exists — most likely
 * another open tab mid-save, so the caller should look again shortly rather
 * than offer it now. Nothing is moved or removed, except copies past the max
 * age.
 *
 * @returns {{ offer: { html: string, ts: number, baseHash?: string } | null, youngPending: boolean }}
 */
export function findLeftoverDrafts(draftId, { exceptVisit, minAgeMs = 0, now = Date.now() } = {}) {
  const own = exceptVisit ? keyFor(draftId, exceptVisit) : null;
  let offer = null;
  let youngPending = false;
  draftKeys(draftId).forEach((key) => {
    if (key === own) return;
    const rec = read(key);
    const age = now - (rec?.ts || 0);
    if (!rec || age > LOCAL_DRAFT_MAX_AGE_MS) { remove(key); return; }
    if (age < minAgeMs) { youngPending = true; return; }
    if (!offer || (rec.ts || 0) > (offer.ts || 0)) offer = rec;
  });
  return { offer, youngPending };
}

/**
 * The author chose Restore or Discard (or the copy turned out to be on the
 * server already): drop what other visits left behind — but with upToTs,
 * only copies no newer than the one acted on. A newer copy was never offered
 * and is still someone's unsaved work.
 */
export function clearLeftoverDrafts(draftId, exceptVisit, { upToTs } = {}) {
  const own = exceptVisit ? keyFor(draftId, exceptVisit) : null;
  draftKeys(draftId).forEach((key) => {
    if (key === own) return;
    const rec = read(key);
    if (!rec || upToTs == null || (rec.ts || 0) <= upToTs) remove(key);
  });
}

/** Drop every snapshot, for any draft, older than the max age. */
export function pruneLocalDrafts(now = Date.now()) {
  try {
    const stale = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${KEY_PREFIX}_`)) continue;
      const rec = read(key);
      if (!rec || now - (rec.ts || 0) > LOCAL_DRAFT_MAX_AGE_MS) stale.push(key);
    }
    stale.forEach(remove);
  } catch {}
}
